import assert from 'minimalistic-assert'
import ms from 'ms'
import { createSelector } from 'reselect'

import { BUMP_RATE } from '../constants.js'
import { isEthereumLike } from '../utils/index.js'

const MINUTE = ms('1m')
const BumpType = {
  NONE: 0,
  CPFP: 1,
  RBF: 2,
}

// Legacy compatibility with wallets
export function getPendingNonExchangeTxs(activeWalletAccount, getTxLog, getIsExchangeTx) {
  console.log('Using deprecated function getPendingNonExchangeTxs()')
  return getAssetPendingNonExchangeTxs('ethereum', activeWalletAccount, getTxLog, getIsExchangeTx)
}

export function getAssetPendingNonExchangeTxs(
  baseAssetName,
  activeWalletAccount,
  getTxLog,
  getIsExchangeTx
) {
  // check if the tx log even has pending transactions
  const result = isQueuedPendingTx(
    { data: { nonce: Number.MAX_SAFE_INTEGER } },
    baseAssetName,
    activeWalletAccount,
    getTxLog
  )
  if (!result) return []

  const txLog = getTxLog(baseAssetName, activeWalletAccount)
  const pendingNonExchangeTxs = []
  for (let index = isQueuedPendingTx.minIndex; index < txLog.size; index++) {
    const _tx = txLog.getAt(index)
    if (_tx.pending && _tx.sent && !getIsExchangeTx(_tx.txId)) {
      pendingNonExchangeTxs.push(_tx)
    }
  }

  return pendingNonExchangeTxs
}

function isQueuedPendingTx(tx, baseAssetName, activeWalletAccount, getTxLog) {
  const txLog = getTxLog(baseAssetName, activeWalletAccount)
  if (!txLog || txLog.size === 0) return false

  const txAtMinIndex = txLog.getAt(isQueuedPendingTx.minIndex)
  if (isQueuedPendingTx.minIndex >= 0 && txAtMinIndex && txAtMinIndex.pending) {
    return tx.data && tx.data.nonce > txAtMinIndex.data.nonce
  }

  let minPendingNonce = Number.MAX_SAFE_INTEGER
  for (let index = 0; index < txLog.size; index++) {
    const _tx = txLog.getAt(index)
    if (!_tx.pending) continue

    const nonce = _tx.data && _tx.data.nonce
    if (nonce === undefined) continue

    // ignore nonce of a TX that just replaced an another tx
    if (_tx.data.replacedTxId && Date.now() - _tx.date < MINUTE) continue
    if (_tx.sent && nonce < minPendingNonce) {
      minPendingNonce = nonce
      isQueuedPendingTx.minIndex = index
    }
  }

  return tx.data.nonce > minPendingNonce
}

// needs for tests
export const _refreshCache = () => {
  isQueuedPendingTx.minPendingNonce = null
  isQueuedPendingTx.minIndex = null
}

const wrapResponseToObject = ({ bumpType = BumpType.NONE, errorMessage = null } = {}) => ({
  bumpType,
  errorMessage,
})

const calculateTxGasPrice = (tx) => tx.feeAmount.div(tx.data.gasLimit)

const calculateBumpedGasPriceNonEip1559 = ({ currentGasPrice, prevMaxFeePerGas }) => {
  assert(currentGasPrice, 'currentGasPrice is required')
  assert(prevMaxFeePerGas, 'prevMaxFeePerGas is required')

  // Pick the largest between the network's `currentGasPrice`
  // and the maximum `gasPrice` the previous transaction was
  // willing to send at.
  const nextGasPrice = currentGasPrice.gt(prevMaxFeePerGas) ? currentGasPrice : prevMaxFeePerGas

  // Suggest using an amplified `gasPrice`.
  return {
    bumpedGasPrice: nextGasPrice.mul(BUMP_RATE),
  }
}

const getLinearIncentiveBumpForEip1559Transaction = ({
  currentBaseFeePerGas,
  prevMaxFeePerGas,
  prevMaxPriorityFeePerGas,
}) => {
  assert(currentBaseFeePerGas, 'currentBaseFeePerGas is required')
  assert(prevMaxFeePerGas, 'prevMaxFeePerGas is required')
  assert(prevMaxPriorityFeePerGas, 'prevMaxPriorityFeePerGas is required')

  // First, let's determine if there's a bump required just to bring
  // the transaction up to the `baseFeePerGas`. If the current base
  // fee is larger, we'll need to see how much to adjust the overall
  // transaction by.
  const increaseToBaseFeePerGasTipGasPrice = currentBaseFeePerGas.gt(prevMaxFeePerGas)
    ? currentBaseFeePerGas.sub(prevMaxFeePerGas)
    : currentBaseFeePerGas.sub(currentBaseFeePerGas) // TODO: What is a nice way to get ZERO without an `asset`? Is it `currentBaseFeePerGas.ZERO`?

  // The new `bumpedTipGasPrice`.
  const bumpedTipGasPrice = prevMaxPriorityFeePerGas
    .mul(BUMP_RATE)
    .add(increaseToBaseFeePerGasTipGasPrice)

  // We'll make attempt a linear increase of the transaction pricing. By
  // increasing the `tipGasPrice` and the `gasPrice` by the same amount,
  // we ensuret he any additional amount we spend (over the basefee) goes
  // directly to the miner, thereby increasing transaction incentive.
  return {
    bumpedGasPrice: prevMaxFeePerGas
      .sub(prevMaxPriorityFeePerGas) // Removes the previous `tipGasPrice` before adding the next one (`bumpedTipGasPrice` is inclusive).
      .add(bumpedTipGasPrice),
    bumpedTipGasPrice,
  }
}

const calculateBumpedGasPriceEip1559 = ({
  currentBaseFeePerGas,
  prevMaxFeePerGas,
  prevMaxPriorityFeePerGas,
}) => {
  assert(currentBaseFeePerGas, 'currentBaseFeePerGas is required')
  assert(prevMaxFeePerGas, 'prevMaxFeePerGas is required')

  // If the previous transaction didn't define a `tipGasPrice`, we
  // can use this value directly.
  if (!prevMaxPriorityFeePerGas || prevMaxPriorityFeePerGas.isZero) {
    // We can determine the intended transaction bump using
    // pre EIP-1559 logic, where we compare the `currentBaseFeePerGas`
    // (i.e. the network gas price without a miner incentive) versus
    // the `currentMaxFeePerGas` (which we can the previous `gasPrice`
    // for the transaction). In this regard, the logic of finding the
    // `bumpedGasPrice` to increase to is identical.
    const { bumpedGasPrice } = calculateBumpedGasPriceNonEip1559({
      currentGasPrice: currentBaseFeePerGas,
      prevMaxFeePerGas,
    })

    assert(bumpedGasPrice >= currentBaseFeePerGas, 'bumpedGasPrice must be >= currentBaseFeePerGas')

    // Since `calculateBumpedGasPriceNonEip1559` will guarantee that
    // the returned `bumpedGasPrice` will be greater than the
    // `currentBaseFeePerGas` (and the `currentMaxFeePerGas`), we can
    // use their difference to deterime the equivalent `bumpTipGasPrice`.
    const bumpedTipGasPrice = bumpedGasPrice.sub(currentBaseFeePerGas)

    return {
      bumpedGasPrice: currentBaseFeePerGas.add(bumpedTipGasPrice),
      bumpedTipGasPrice,
    }
  }

  // Else, let's make a linear increase to the transaction tip. This
  // increase should ensure that:
  // 1. The `prevMaxPriorityFeePerGas` is multiplied by the `BUMP_RATE`.
  // 2. The returned `bumpedGasPrice` is sufficient for the
  //    `currentBaseFeePerGas`.
  return getLinearIncentiveBumpForEip1559Transaction({
    currentBaseFeePerGas,
    prevMaxFeePerGas,
    prevMaxPriorityFeePerGas,
  })
}

export const calculateBumpedGasPrice = ({
  baseAsset,
  tx,
  currentBaseFee,
  currentGasPrice,
  eip1559Enabled,
}) => {
  // Determine the transaction we intend to override's
  // `maxFeePerGas` (for pre-EIP-1559 networks, this is the
  // `gasPrice` of the previous transaction).
  const prevMaxFeePerGas = calculateTxGasPrice(tx)

  if (!eip1559Enabled)
    return calculateBumpedGasPriceNonEip1559({
      currentGasPrice,
      prevMaxFeePerGas,
    })

  if (!currentBaseFee) {
    // NOTE: This can result in differences between predicted and realized fees.
    currentBaseFee = currentGasPrice
  }

  const prevMaxPriorityFeePerGas = tx.data?.tipGasPrice
    ? baseAsset.currency.baseUnit(tx.data.tipGasPrice)
    : null

  return calculateBumpedGasPriceEip1559({
    baseAsset,
    currentBaseFeePerGas: currentBaseFee,
    prevMaxPriorityFeePerGas,
    prevMaxFeePerGas,
  })
}

export const canAccelerateTx = ({
  tx,
  assets,
  getFeeData,
  activeWalletAccount,
  getPersonalNoteByTxId,
  getIsRbfEnabled,
  getIsEnoughBalanceToAccelerate,
  getTxLog,
  getIsExchangeTx,
}) => {
  const assetName = tx.coinName
  const baseAsset = assets[assetName].baseAsset
  const baseAssetName = baseAsset.name
  if (!getIsRbfEnabled(assetName)) return wrapResponseToObject({ errorMessage: 'rbf is disabled' })
  if (!isEthereumLike(assets[assetName]))
    return wrapResponseToObject({ errorMessage: `not an ETH/ERC20/BSC/BEP20 asset supplied` })
  if (!tx.pending || !tx.sent)
    return wrapResponseToObject({ errorMessage: 'can not bump a confirmed or received TX' })
  if (!tx.data || !(tx.data.gasLimit || tx.data.tipGasPrice))
    return wrapResponseToObject({ errorMessage: 'data object is missing or corrupted' })
  const isExchangeTx = getIsExchangeTx(tx.txId)
  if (isExchangeTx) return wrapResponseToObject({ errorMessage: 'can not bump an exchange TX' })
  const personalNote = getPersonalNoteByTxId(tx.txId)
  if (personalNote && personalNote.dapp)
    return wrapResponseToObject({ errorMessage: 'can not bump a dapp TX' })
  if (isQueuedPendingTx(tx, baseAssetName, activeWalletAccount, getTxLog))
    return wrapResponseToObject({ errorMessage: 'there is a stuck TX with lower nonce' })

  const {
    gasPrice: currentGasPrice,
    eip1559Enabled,
    baseFeePerGas: currentBaseFee,
  } = getFeeData(assetName)

  const { bumpedGasPrice: gasPriceToUse } = calculateBumpedGasPrice({
    baseAsset,
    tx,
    currentGasPrice,
    currentBaseFee,
    eip1559Enabled,
  })
  const replacementFee = gasPriceToUse.mul(tx.data.gasLimit)

  const extraEthNeeded = replacementFee.sub(tx.feeAmount)

  if (!getIsEnoughBalanceToAccelerate(activeWalletAccount, baseAssetName, extraEthNeeded))
    return wrapResponseToObject({ errorMessage: 'insufficient funds' })

  return wrapResponseToObject({ bumpType: BumpType.RBF })
}

const getCanAccelerateTxFactory = (
  getFeeDataSelector,
  getFeeSelector,
  activeWalletAccountSelector,
  getPersonalNoteByTxIdSelector,
  getIsRbfEnabledSelector,
  getIsEnoughBalanceToAccelerateSelector,
  getTxLogSelector,
  getIsExchangeTxSelector,
  assetsSelector
) =>
  createSelector(
    getFeeDataSelector,
    getFeeSelector,
    activeWalletAccountSelector,
    getPersonalNoteByTxIdSelector,
    getIsRbfEnabledSelector,
    getIsEnoughBalanceToAccelerateSelector,
    getTxLogSelector,
    getIsExchangeTxSelector,
    assetsSelector,
    (
      getFeeData,
      getFee,
      activeWalletAccount,
      getPersonalNoteByTxId,
      getIsRbfEnabled,
      getIsEnoughBalanceToAccelerate,
      getTxLog,
      getIsExchangeTx,
      assets
    ) =>
      (tx) =>
        canAccelerateTx({
          tx,
          assets,
          getFeeData,
          getFee,
          activeWalletAccount,
          getPersonalNoteByTxId,
          getIsRbfEnabled,
          getIsEnoughBalanceToAccelerate,
          getTxLog,
          getIsExchangeTx,
        })
  )

export default getCanAccelerateTxFactory
